import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FormField from '@/lib/components/FormField/FormField';
import Button from '@/lib/components/Button';
import { useFormSave } from '@/lib/hooks/useFormSave';

import styles from '@/lib/components/PspComposer/PspComposer.module.scss';

const CURP_REGEX = /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/;
const RFC_REGEX = /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/;
/**
 * This component is one of the multiple available payment method in this project.
 * It represents a CLABE (mexican bank system) form, only used to collect refund request data for now.
 */
const Clabe = React.forwardRef(
    (
        {
            pspId,
            token,
            paymentRequestId,
            lyriaUrl,
            hidePayButton = false,
            labelPayButton = null,  // Fallback on 'pay' or 'ask refund' label.
            onPaymentResult,
        },
        ref,
    ) => {
        const { t } = useTranslation();

        const formRef = React.useRef(null);
        const [formData, setFormData] = React.useState({
            firstName: '',
            lastName: '',
            clabeNumber: '',
            curpNumber: '',
            rfcNumber: '',
            phoneNumber: '',
            bankCardType: '',
        });
        const [formFieldBlurState, setFormFieldBlurState] = React.useState({
            firstName: false,
            lastName: false,
            clabeNumber: false,
            curpNumber: false,
            rfcNumber: false,
            phoneNumber: false,
            bankCardType: false,
        });
        const [errors, setErrors] = React.useState({});

        const handleSuccess = React.useCallback(
            (response) => {
                onPaymentResult({ success: true, data: response});
            },
            [onPaymentResult],
        );

        const handleError = React.useCallback(
            (response) => {
                if (response?.data) {
                    const fieldErrors = {};
                    Object
                        .keys(response.data)
                        .forEach((key) => {
                            fieldErrors[key] = response.data[key] && response.data[key].map((error) => t([
                                `clabe_${key}_error_${error.code}`,
                                `error_${error.code}`,
                                'error_form',
                            ]));
                        });
                    setErrors(fieldErrors);
                }
                onPaymentResult({
                    success: false,
                    data: response,
                });
            },
            [onPaymentResult, t],
        );

        const url = lyriaUrl + '/clabe/save';
        const saveForm = useFormSave(url, token, paymentRequestId, pspId, handleSuccess, handleError);

        const submitForm = React.useCallback(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                const data = {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    clabe_number: formData.clabeNumber,
                    curp_number: formData.curpNumber,
                    rfc_number: formData.rfcNumber,
                    phone_number: formData.phoneNumber,
                    bank_card_type: formData.bankCardType,
                };
                saveForm(data);
            },
            [formData, saveForm],
        );
        const formatAndValidateNames = React.useCallback(
            (value) => {
                const newValue = (value ?? '').replace(/[^A-Za-zÀ-ÿ '-]/g, '').slice(0, 50);
                return newValue;
            },
            [],
        );
        const formatAndValidateClabe = React.useCallback(
            (value) => {
                let newValue = value.toUpperCase().replace(/\s+/g, '');
                let matches = newValue.match(/[0-9]+/g);
                newValue = (matches && matches.join('')) || '';

                setErrors((prevState) => ({
                    ...prevState,
                    clabe_number: newValue.length !== 18 ? [t('clabe_clabe_number_error_min_length')] : null,
                }));

                return newValue;
            },
            [t],
        );

        const formatAndValidateCurp = React.useCallback(
            (value) => {
                let newValue = value.toUpperCase().replace(/\s+/g, '');
                let matches = newValue.substr(0, 18).match(/[A-Z0-9]/g);
                newValue = matches?.join('') || '';

                let curpError = null;
                if (newValue.length !== 18) {
                    curpError = [t('clabe_curp_number_error_min_length')];
                } else if (!newValue.match(CURP_REGEX)) {
                    curpError = [t('clabe_curp_number_error_invalid')];
                }

                setErrors((prevState) => ({
                    ...prevState,
                    curp_number: curpError,
                }));

                return newValue;
            },
            [t],
        );

        const formatAndValidateRfc = React.useCallback(
            (value) => {
                const newValue = (value ?? '').toUpperCase().replace(/[^A-Z0-9ÑÁÉÍÓÚÜ]/g, '').slice(0, 13);
                
                let rfcError = null;
                if (newValue.length !== 12 && newValue.length !== 13) {
                    rfcError = [t('clabe_rfc_value_error_length')];
                } else if (!newValue.match(RFC_REGEX)) {
                    rfcError = [t('clabe_rfc_value_error_invalid')];
                }
                
                setErrors((prevState) => ({
                    ...prevState,
                    rfc_number: rfcError,
                }));
                return newValue;
            },
            [t],
        );

        const onChangeFormField = React.useCallback(
            (fieldName) => (event, newValue) => {
                setFormData((prevState) => ({
                    ...prevState,
                    [fieldName]: newValue,
                }));
            },
            [],
        );

        const onBlurFormField = React.useCallback(
            (fieldName) => (_event) => {
                setFormFieldBlurState((prevState) => ({
                    ...prevState,
                    [fieldName]: true,
                }));
            },
            [],
        );

        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    return formRef.current.click();
                },
                canClickOnPay: Object.values(formData).every((value) => !!value),
            }),
            [formData],
        );

        return (
            <div id="wz-lyriapay__clabe" className={styles.common_psp}>
                <form noValidate onSubmit={submitForm}>
                    <div className={styles.common_psp_form}>
                        <div className={styles.common_field_group}>
                            <FormField
                                name="first_name"
                                placeholder={t('clabe_placeholder_first_name')}
                                value={formData.firstName}
                                onChange={onChangeFormField('firstName')}
                                label={t('clabe_first_name')}
                                errors={errors.first_name}
                                required
                                formatAndValidate={formatAndValidateNames}
                            />
                            <FormField
                                name="last_name"
                                placeholder={t('clabe_placeholder_last_name')}
                                value={formData.lastName}
                                onChange={onChangeFormField('lastName')}
                                label={t('clabe_last_name')}
                                errors={errors.last_name}
                                required
                                formatAndValidate={formatAndValidateNames}
                            />
                        </div>
                        <FormField
                            name="clabe_number"
                            placeholder={t('clabe_placeholder_clabe_number')}
                            value={formData.clabeNumber}
                            onChange={onChangeFormField('clabeNumber')}
                            onBlur={onBlurFormField('clabeNumber')}
                            label={t('clabe_clabe_number')}
                            formatAndValidate={formatAndValidateClabe}
                            errors={
                                (errors.clabe_number?.length || 0) > 1
                                    ? formFieldBlurState.clabeNumber ? [errors.clabe_number[0]] : null
                                    : formFieldBlurState.clabeNumber ? errors.clabe_number : null
                            }
                            required
                        />
                        <FormField
                            name="curp_number"
                            placeholder={t('clabe_placeholder_curp_number')}
                            value={formData.curpNumber}
                            onChange={onChangeFormField('curpNumber')}
                            onBlur={onBlurFormField('curpNumber')}
                            label={t('clabe_curp_number')}
                            formatAndValidate={formatAndValidateCurp}
                            errors={
                                (errors.curp_number?.length || 0) > 1
                                    ? formFieldBlurState.curpNumber ? [errors.curp_number[0]] : null
                                    : formFieldBlurState.curpNumber ? errors.curp_number : null
                            }
                            required
                        />
                        <FormField
                            name="rfc_number"
                            placeholder={t('clabe_placeholder_rfc_value')}
                            value={formData.rfcValue}
                            onChange={onChangeFormField('rfcNumber')}
                            onBlur={onBlurFormField('rfcNumber')}
                            label={t('clabe_rfc_value')}
                            formatAndValidate={formatAndValidateRfc}
                            errors={
                                (errors.rfc_number?.length || 0) > 1
                                    ? formFieldBlurState.rfcNumber ? [errors.rfc_number[0]] : null
                                    : formFieldBlurState.rfcNumber ? errors.rfc_number : null
                            }
                            required
                        />
                        <FormField
                            name="phone_number"
                            placeholder={t('clabe_placeholder_phone_number')}
                            value={formData.phoneNumber}
                            onChange={onChangeFormField('phoneNumber')}
                            label={t('clabe_phone_number')}
                            errors={errors.phone_number}
                            required
                        />
                        <FormField
                            name="bank_card_type"
                            value={formData.bankCardType}
                            onChange={onChangeFormField('bankCardType')}
                            label={t('clabe_bank_card_type')}
                            errors={errors.bank_card_type}
                            type="select"
                            required
                            options={[
                                { value: '', label: '' },
                                { value: 'credit', label: t('clabe_bank_card_type_type_credit') },
                                { value: 'debit', label: t('clabe_bank_card_type_type_debit') },
                            ]}
                        />
                    </div>
                    {!hidePayButton && (
                        <div className={styles.common_psp_submit}>
                            <Button
                                disabled={Object.values(formData).some((value) => !value) || formData.clabeNumber.length !== 18 || formData.curpNumber.length !== 18}
                                label={labelPayButton || t('payButton')}
                                ref={formRef}
                                type='submit'
                            />
                        </div>
                    )}
                </form>
            </div>
        );
    },
);

Clabe.propTypes = {
    /**
     * The id of the PSP returned by the backend in order to be able to pass it when we submit the form data.
     */
    pspId: PropTypes.number.isRequired,
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
    * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
    */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
};

Clabe.displayName = 'Clabe';

export default Clabe;
